import { useEffect, useState } from "react";
import { getFiles, deleteFile } from "../../../Services/projectServices";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";
import { TOAST_MESSAGES } from "../constants";
import { CommonTable, Spinner } from "../../../Components/Common";
import InfiniteScroll from "react-infinite-scroll-component";
import { MdDownload } from "react-icons/md";
import { FiTrash2 } from "react-icons/fi";
import { downloadUtil } from "../../../Utils/DownloadUtil";

const AttachmentPopup = ({
  onClose,
  getMilestone,
  currentMilestoneStatus,
}) => {
  const RESET_PAGINATION = { offset: 0, limit: 5, count: 0 };
  const [fileData, setFileData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paginationLoading, setPaginationLoading] = useState(false);
  const [pagination, setPagination] = useState(RESET_PAGINATION);
  const { projectId } = useParams();

  const fetchFileData = async (
    offset = 0,
    limit = RESET_PAGINATION.limit,
    isInitial = false
  ) => {
    if (isInitial) {
      setLoading(true);
    } else {
      setPaginationLoading(true);
    }

    try {
      const response = await getFiles(projectId, {
        milestone: getMilestone,
        offset,
        limit,
      });
      if (response.status === 200) {
        const newFiles = response.data.results || [];
        setFileData((prev) => (isInitial ? newFiles : [...prev, ...newFiles]));
        setPagination({
          offset: offset + limit,
          limit: RESET_PAGINATION.limit,
          count: response.data.count || 0,
        });
      } else {
        toast.error(TOAST_MESSAGES.FILES_FETCHED_ERROR);
      }
    } catch (error) {
      toast.error(TOAST_MESSAGES.FILES_FETCHED_ERROR);
    } finally {
      if (isInitial) setLoading(false);
      else setPaginationLoading(false);
    }
  };

  useEffect(() => {
    fetchFileData(0, RESET_PAGINATION.limit, true);
  }, []);

  const loadMoreFiles = () => {
    if (pagination.offset >= pagination.count) return;
    fetchFileData(pagination.offset, RESET_PAGINATION.limit);
  };

  const handleDownload = async (fileUrl) => {
    try {
      await downloadUtil(fileUrl);
      toast.success(TOAST_MESSAGES.FILE_DOWNLOAD_SUCCESSFUL);
    } catch (error) {
      toast.error(TOAST_MESSAGES.FILE_DOWNLOAD_FAIL);
    }
  };

  const handleDelete = async (fileId) => {
    console.log("Current milestone status=>", currentMilestoneStatus)
    try {
      setLoading(true);
      const response = await deleteFile(projectId, fileId);
      if (response.status === 204 || response.status === 200) {
        toast.success(TOAST_MESSAGES.FILE_DELETE_SUCCESS);
        fetchFileData(0, RESET_PAGINATION.limit, true);
      } else {
        toast.error(TOAST_MESSAGES.FILE_DELETE_FAIL);
      }
    } catch (error) {
      toast.error(TOAST_MESSAGES.ERROR_DELETE_FILE);
    } finally {
      setLoading(false);
    }
  };

  const handleRenderField = (key, rowData) => {
    if (key === "actions") {
      return (
        <div key={rowData.id} className="flex items-center justify-start gap-6">
          {/* Download button */}
          <button
            onClick={() => handleDownload(rowData.file)}
            title="Download File"
          >
            <MdDownload
              size={20}
              className="text-gray-500 hover:text-blue-600"
            />
          </button>

          {/* Delete button */}
          <button
            disabled={currentMilestoneStatus === "COMPLETED" ? true : false}
            onClick={() => handleDelete(rowData.id)}
            title={`${
              currentMilestoneStatus === "COMPLETED"
                ? "Can not delete, already completed"
                : "Delete file"
            }`}
            className={`${
              currentMilestoneStatus === "COMPLETED"
                ? "text-gray-400 cursor-not-allowed"
                : "text-red-500 hover:text-blue-600"
            }`}
          >
            <FiTrash2 size={20} />
          </button>
        </div>
      );
    }

    if (key === "file") {
      const fileName = rowData.file?.split("/").pop().split("?")[0];
      return (
        <a
          href={rowData.file}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:underline"
          title={fileName}
        >
          {fileName || "N/A"}
        </a>
      );
    }

    return rowData[key] || "N/A";
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center py-3 px-4 border-b">
          <h2 className="text-md font-semibold">File Details</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-800 text-xl"
          >
            ✕
          </button>
        </div>

        <div
          id="fileScrollDiv"
          className="h-80 overflow-x-hidden overflow-y-scroll thin-scrollbar p-4"
        >
          {loading ? (
            <div className="flex justify-center items-center">
              <Spinner />
            </div>
          ) : (
            <InfiniteScroll
              dataLength={fileData.length}
              next={loadMoreFiles}
              hasMore={pagination.offset < pagination.count}
              scrollableTarget="fileScrollDiv"
              loader={
                paginationLoading && (
                  <div className="flex justify-center py-2">
                    <Spinner size={3} />
                  </div>
                )
              }
            >
              <CommonTable
                tableColumns={["file", "actions"]}
                columnLabels={{ file: "File", actions: "Actions" }}
                tableRows={fileData}
                renderField={handleRenderField}
                className={"border-2 shadow-md text-md"}
              />
            </InfiniteScroll>
          )}
        </div>
      </div>
    </div>
  );
};

export default AttachmentPopup;
